import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { REVIEW_RATINGS } from '../../util/types';
import { HR_STRATEGIC_SKILL_CATEGORIES } from '../../marketplace-custom-config';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import IconReviewStar from '../IconReviewStar/IconReviewStar';

import css from './HRSkillsRatingModal.module.css';

const StarRatingInput = ({ skillKey, value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  const current = value ? parseInt(value, 10) : 0;
  const display = hovered || current;

  const handleMouseLeave = () => setHovered(0);

  return (
    <div className={css.starRow} onMouseLeave={handleMouseLeave}>
      {REVIEW_RATINGS.map(star => {
        const handleClick = () => onChange(String(star));
        const handleMouseEnter = () => setHovered(star);
        return (
          <button
            key={star}
            type="button"
            className={css.starButton}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            aria-label={`${star} star`}
          >
            <IconReviewStar
              isFilled={star <= display}
              className={classNames(css.starIcon, { [css.starIconFilled]: star <= display })}
            />
          </button>
        );
      })}
    </div>
  );
};

const { string, func } = PropTypes;

StarRatingInput.propTypes = {
  skillKey: string.isRequired,
  value: string,
  onChange: func.isRequired,
};

StarRatingInput.defaultProps = {
  value: null,
};

const HRSkillsRatingModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    hrSkills,
    onConfirm,
    updateInProgress,
  } = props;

  const selectedKeys = Object.keys(hrSkills || {});

  const relevantCategories = HR_STRATEGIC_SKILL_CATEGORIES
    .map(cat => ({
      ...cat,
      skills: cat.skills.filter(s => selectedKeys.includes(s.key)),
    }))
    .filter(cat => cat.skills.length > 0);

  const handleSubmit = values => {
    onConfirm(values.ratings);
  };

  return (
    <Modal
      id="HRSkillsRatingModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div className={css.modalContent}>
        <h2 className={css.title}>
          <FormattedMessage id="HRSkillsRatingModal.title" />
        </h2>
        <p className={css.description}>
          <FormattedMessage id="HRSkillsRatingModal.description" />
        </p>
        <p className={css.boldNote}>
          <strong><FormattedMessage id="HRSkillsRatingModal.oneStarLabel" /></strong>{' '}<FormattedMessage id="HRSkillsRatingModal.oneStarText" />
        </p>
        <p className={css.boldNote}>
          <strong><FormattedMessage id="HRSkillsRatingModal.fiveStarLabel" /></strong>{' '}<FormattedMessage id="HRSkillsRatingModal.fiveStarText" />
        </p>

        <FinalForm
          key={JSON.stringify(hrSkills)}
          initialValues={{ ratings: hrSkills }}
          onSubmit={handleSubmit}
          render={({ handleSubmit: formHandleSubmit, values }) => {
            const ratings = values.ratings || {};
            const allRated =
              selectedKeys.length > 0 &&
              selectedKeys.every(
                k => ratings[k] !== null && ratings[k] !== undefined && ratings[k] !== ''
              );

            return (
              <form onSubmit={formHandleSubmit}>
                <div className={css.skillsList}>
                  {relevantCategories.map(category => (
                    <div key={category.key} className={css.categorySection}>
                      <h3 className={css.categoryLabel}>{category.label}</h3>
                      {category.skills.map(skill => (
                        <div key={skill.key} className={css.skillRow}>
                          <span className={css.skillName}>{skill.label}</span>
                          <Field name={`ratings.${skill.key}`}>
                            {({ input }) => (
                              <StarRatingInput
                                skillKey={skill.key}
                                value={input.value || null}
                                onChange={input.onChange}
                              />
                            )}
                          </Field>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <Button
                  type="submit"
                  className={css.saveButton}
                  disabled={!allRated || updateInProgress}
                  inProgress={updateInProgress}
                >
                  <FormattedMessage id="HRSkillsRatingModal.save" />
                </Button>
              </form>
            );
          }}
        />
      </div>
    </Modal>
  );
};

const { bool, object, func: funcProp } = PropTypes;

HRSkillsRatingModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: funcProp.isRequired,
  onManageDisableScrolling: funcProp.isRequired,
  hrSkills: object.isRequired,
  onConfirm: funcProp.isRequired,
  updateInProgress: bool,
};

HRSkillsRatingModal.defaultProps = {
  updateInProgress: false,
};

export default HRSkillsRatingModal;
